import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/api";

/* `/api/auth/me` était appelé par chaque page EN PLUS de l'en-tête, et l'en-tête le
   redemandait à chaque changement de route : pour un visiteur anonyme cela faisait deux
   401 par chargement dans la console. Un visiteur anonyme n'est pas une erreur : le
   serveur répond maintenant 200 avec `{"user": null}`, et la lecture est unique et
   partagée ici. `cached === undefined` = pas encore lu ; `null` = anonyme, confirmé par
   le serveur. Une vraie erreur réseau ne déconnecte personne : elle ne réécrit rien. */

const AuthContext = createContext({ me: null, loaded: false, refreshMe: () => {}, clearMe: () => {} });

let cached;
let inflight = null;

const normalise = (data) => (data && data.user === null ? null : data);

export function fetchMe(force = false) {
  if (!force && cached !== undefined) return Promise.resolve(cached);
  if (force || !inflight) {
    inflight = api.get("/auth/me")
      .then((r) => { cached = normalise(r.data); return cached; })
      .catch(() => (cached === undefined ? null : cached))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function AuthProvider({ children }) {
  const [me, setMe] = useState(cached ?? null);
  const [loaded, setLoaded] = useState(cached !== undefined);

  useEffect(() => {
    if (cached !== undefined) return;
    let alive = true;
    fetchMe().then((u) => { if (alive) { setMe(u); setLoaded(true); } });
    return () => { alive = false; };
  }, []);

  const refreshMe = useCallback(async () => {
    const u = await fetchMe(true);
    setMe(u);
    setLoaded(true);
    return u;
  }, []);

  const clearMe = useCallback(() => {
    cached = null;
    setMe(null);
    setLoaded(true);
  }, []);

  return <AuthContext.Provider value={{ me, loaded, refreshMe, clearMe }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
